import { useEffect, useMemo, useState } from "react";
import type { Key, ReactNode } from "react";
import { Button, Checkbox, Empty, Popover, Space, Table, Tooltip } from "antd";
import type { TablePaginationConfig, TableProps } from "antd";
import type { ColumnsType, ColumnType, SorterResult } from "antd/es/table/interface";
import { DownloadOutlined, SettingOutlined } from "@ant-design/icons";

export type BulkAction<T> = {
  key: string;
  label: ReactNode;
  icon?: ReactNode;
  danger?: boolean;
  onClick: (ids: Key[], rows: T[]) => Promise<void> | void;
};

export type EnhancedTableProps<T> = {
  rowKey: TableProps<T>["rowKey"];
  dataSource: readonly T[] | T[] | undefined;
  columns: ColumnsType<T>;
  loading?: boolean;
  pagination?: TableProps<T>["pagination"];
  onChange?: TableProps<T>["onChange"];
  size?: TableProps<T>["size"];
  persistKey?: string;
  exportFilename?: string;
  toolbarLeft?: ReactNode;
  toolbarRight?: ReactNode;
  rowSelection?: TableProps<T>["rowSelection"];
  bulkActions?: BulkAction<T>[];
  onBulkComplete?: () => void;
  emptyText?: string;
  emptyAction?: ReactNode;
};

type StoredSort = {
  field: string;
  order: "ascend" | "descend";
};

function safeParse<TVal>(raw: string | null): TVal | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as TVal;
  } catch {
    return null;
  }
}

function readStorage<TVal>(key: string | undefined): TVal | null {
  if (!key) return null;
  if (typeof window === "undefined") return null;
  try {
    return safeParse<TVal>(window.localStorage.getItem(key));
  } catch {
    return null;
  }
}

function writeStorage(key: string | undefined, value: unknown): void {
  if (!key) return;
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore quota / privacy errors
  }
}

type AnyColumn = {
  key?: Key;
  dataIndex?: Key | readonly Key[];
  title?: unknown;
};

function getColumnId(col: unknown, index: number): string {
  const c = col as AnyColumn;
  if (c.key != null) return String(c.key);
  if (c.dataIndex != null) {
    return Array.isArray(c.dataIndex)
      ? (c.dataIndex as Key[]).join(".")
      : String(c.dataIndex);
  }
  return `col-${index}`;
}

function getColumnTitle(col: unknown): string {
  const t = (col as AnyColumn).title;
  if (typeof t === "string") return t;
  if (typeof t === "number") return String(t);
  return "";
}

function escapeCsv(value: unknown): string {
  if (value == null) return "";
  const str = typeof value === "string" ? value : String(value);
  if (/[",\r\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function getCellExportValue<T>(record: T, col: ColumnType<T>): unknown {
  const di = col.dataIndex;
  if (di == null) return "";
  if (Array.isArray(di)) {
    let cur: unknown = record;
    for (const part of di) {
      if (cur == null) return "";
      cur = (cur as Record<string, unknown>)[String(part)];
    }
    return cur;
  }
  return (record as Record<string, unknown>)[String(di)];
}

function downloadCsv(filename: string, csv: string): void {
  if (typeof window === "undefined") return;
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function EnhancedTable<T extends object>(props: EnhancedTableProps<T>): JSX.Element {
  const {
    rowKey,
    dataSource,
    columns,
    loading,
    pagination,
    onChange,
    size,
    persistKey,
    exportFilename,
    toolbarLeft,
    toolbarRight,
    rowSelection,
    bulkActions,
    onBulkComplete,
    emptyText,
    emptyAction,
  } = props;

  const colsKey = persistKey ? `${persistKey}:cols` : undefined;
  const sortKey = persistKey ? `${persistKey}:sort` : undefined;

  const allColumnIds = useMemo(
    () => columns.map((c, i) => getColumnId(c, i)),
    [columns],
  );

  const [hiddenCols, setHiddenCols] = useState<string[]>(() => {
    const stored = readStorage<string[]>(colsKey);
    return Array.isArray(stored) ? stored : [];
  });

  const [storedSort, setStoredSort] = useState<StoredSort | null>(() =>
    readStorage<StoredSort>(sortKey),
  );

  const [internalSelectedKeys, setInternalSelectedKeys] = useState<Key[]>([]);
  const [bulkLoadingKey, setBulkLoadingKey] = useState<string | null>(null);

  // Controlled vs uncontrolled selection: respect caller's selectedRowKeys if provided.
  const selectedKeys: Key[] = rowSelection?.selectedRowKeys
    ? [...rowSelection.selectedRowKeys]
    : internalSelectedKeys;

  const mergedRowSelection: TableProps<T>["rowSelection"] | undefined = rowSelection
    ? {
        ...rowSelection,
        selectedRowKeys: selectedKeys,
        onChange: (keys, rows, info) => {
          setInternalSelectedKeys([...keys]);
          rowSelection.onChange?.(keys, rows, info);
        },
      }
    : undefined;

  const visibleColumns = useMemo<ColumnsType<T>>(() => {
    return columns
      .map((c, i) => {
        const id = getColumnId(c, i);
        if (hiddenCols.includes(id)) return null;
        if (storedSort && storedSort.field === id) {
          // Apply persisted sort order as default. Only honor when column is sortable.
          const col = c as ColumnType<T>;
          if (col.sorter) {
            return { ...col, defaultSortOrder: storedSort.order } as ColumnType<T>;
          }
        }
        return c;
      })
      .filter((c): c is ColumnsType<T>[number] => c !== null);
  }, [columns, hiddenCols, storedSort]);

  // Persist hidden columns whenever changed.
  useEffect(() => {
    if (!colsKey) return;
    writeStorage(colsKey, hiddenCols);
  }, [colsKey, hiddenCols]);

  const handleChange: TableProps<T>["onChange"] = (pag, filters, sorter, extra) => {
    if (sortKey) {
      const s = Array.isArray(sorter) ? sorter[0] : (sorter as SorterResult<T> | undefined);
      if (s && s.order && s.field != null) {
        const field = Array.isArray(s.field) ? s.field.join(".") : String(s.field);
        const next: StoredSort = { field, order: s.order };
        setStoredSort(next);
        writeStorage(sortKey, next);
      } else {
        setStoredSort(null);
        if (typeof window !== "undefined") {
          try {
            window.localStorage.removeItem(sortKey);
          } catch {
            // ignore
          }
        }
      }
    }
    onChange?.(pag as TablePaginationConfig, filters, sorter, extra);
  };

  const toggleColumn = (id: string, checked: boolean) => {
    setHiddenCols((prev) => {
      if (checked) return prev.filter((x) => x !== id);
      if (prev.includes(id)) return prev;
      return [...prev, id];
    });
  };

  const handleExport = () => {
    if (!exportFilename) return;
    const exportCols = visibleColumns.filter(
      (c) => (c as ColumnType<T>).dataIndex != null,
    ) as ColumnType<T>[];
    const header = exportCols.map((c) => escapeCsv(getColumnTitle(c))).join(",");
    const rows = (dataSource ?? []).map((row) =>
      exportCols.map((c) => escapeCsv(getCellExportValue(row, c))).join(","),
    );
    const csv = [header, ...rows].join("\r\n");
    const date = new Date().toISOString().slice(0, 10);
    downloadCsv(`${exportFilename}-${date}.csv`, csv);
  };

  // Resolve selected row objects from current dataSource for bulk action handlers.
  const resolveRowKey = (row: T): Key => {
    if (typeof rowKey === "function") return rowKey(row);
    const k = rowKey as keyof T | undefined;
    if (k == null) return "" as Key;
    return row[k] as unknown as Key;
  };

  const selectedRows = useMemo(() => {
    const ds = dataSource ?? [];
    if (selectedKeys.length === 0) return [] as T[];
    const set = new Set(selectedKeys.map((k) => String(k)));
    return ds.filter((row) => set.has(String(resolveRowKey(row))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSource, selectedKeys, rowKey]);

  const clearSelection = () => {
    setInternalSelectedKeys([]);
    rowSelection?.onChange?.([], [], { type: "all" as never });
  };

  const runBulkAction = async (action: BulkAction<T>) => {
    try {
      setBulkLoadingKey(action.key);
      await action.onClick(selectedKeys, selectedRows);
      clearSelection();
      onBulkComplete?.();
    } finally {
      setBulkLoadingKey(null);
    }
  };

  const columnManager = (
    <div style={{ minWidth: 180, maxHeight: 320, overflowY: "auto" }}>
      {columns.map((c, i) => {
        const id = getColumnId(c, i);
        const title = getColumnTitle(c) || id;
        const checked = !hiddenCols.includes(id);
        return (
          <div key={id} style={{ padding: "4px 0" }}>
            <Checkbox
              checked={checked}
              onChange={(e) => toggleColumn(id, e.target.checked)}
            >
              {title}
            </Checkbox>
          </div>
        );
      })}
    </div>
  );

  const showEmpty = !loading && (dataSource?.length ?? 0) === 0;

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          marginBottom: 8,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>{toolbarLeft}</div>
        <Space>
          {toolbarRight}
          {exportFilename ? (
            <Tooltip title="导出 CSV">
              <Button
                aria-label="export-csv"
                icon={<DownloadOutlined />}
                onClick={handleExport}
              />
            </Tooltip>
          ) : null}
          <Popover
            trigger="click"
            placement="bottomRight"
            content={columnManager}
            title="显示列"
          >
            <Tooltip title="列设置">
              <Button aria-label="column-manager" icon={<SettingOutlined />} />
            </Tooltip>
          </Popover>
        </Space>
      </div>

      {showEmpty ? (
        <div style={{ padding: "48px 0", textAlign: "center" }}>
          <Empty description={emptyText} />
          {emptyAction ? <div style={{ marginTop: 16 }}>{emptyAction}</div> : null}
        </div>
      ) : (
        <Table<T>
          rowKey={rowKey}
          dataSource={dataSource as T[] | undefined}
          columns={visibleColumns}
          loading={loading}
          pagination={pagination}
          onChange={handleChange}
          size={size}
          rowSelection={mergedRowSelection}
        />
      )}

      {mergedRowSelection && selectedKeys.length > 0 ? (
        <div
          role="region"
          aria-label="bulk-action-bar"
          style={{
            position: "sticky",
            bottom: 0,
            background: "#fff",
            borderTop: "1px solid #f0f0f0",
            padding: "12px 16px",
            zIndex: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginTop: 8,
          }}
        >
          <Space>
            <span>已选 {selectedKeys.length} 项</span>
            <Button size="small" onClick={clearSelection}>
              取消
            </Button>
          </Space>
          <Space>
            {(bulkActions ?? []).map((action) => (
              <Button
                key={action.key}
                danger={action.danger}
                icon={action.icon}
                loading={bulkLoadingKey === action.key}
                onClick={() => runBulkAction(action)}
              >
                {action.label}
              </Button>
            ))}
          </Space>
        </div>
      ) : null}
    </div>
  );
}

export default EnhancedTable;
